import React, { useCallback, useEffect, useState } from "react";
import api from "../utils/api";

const REASON_LABEL = {
  SPAM: "Spam", HARASSMENT: "Harassment", HATE: "Hate speech", SEXUAL: "Sexual content", VIOLENCE: "Violence",
  CHILD_SAFETY: "Child safety", COPYRIGHT: "Copyright", PRIVACY: "Privacy", MISINFORMATION: "Misleading", OTHER: "Other",
};

const fmt = (d) => (d ? new Date(d.replace(" ", "T")).toLocaleString() : "");

/**
 * Admin → Reports: user/guest reports (IT Rules 2021: acknowledge in 24h, resolve in 15 days)
 * and closed accounts awaiting the permanent purge (privacy policy: within 30 days).
 */
export default function AdminReportsTab() {
  const [status, setStatus] = useState("OPEN");
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState({});
  const [purges, setPurges] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get(`/api/admin/reports?status=${status}`),
        api.get("/api/admin/account-purges"),
      ]);
      setReports(Array.isArray(r.data) ? r.data : []);
      setPurges(Array.isArray(p.data) ? p.data : []);
    } catch {
      setErr("Could not load reports.");
    }
    setLoading(false);
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, newStatus) => {
    setErr(""); setMsg("");
    try {
      await api.put(`/api/admin/reports/${id}`, { status: newStatus, adminNote: notes[id] ?? undefined });
      setMsg(`REP-${id} marked ${newStatus.toLowerCase()}.`);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || "Could not update the report.");
    }
  };

  const purge = async (userId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Permanently delete all content, comments and uploaded files of closed account #${userId}? Payment and order records are kept. This can't be undone.`)) return;
    setErr(""); setMsg("");
    try {
      const r = await api.delete(`/api/admin/users/${userId}/purge`);
      setMsg(`Account #${userId} purged: ${r.data.contentItemsDeleted} content items, ${r.data.filesDeleted} of ${r.data.filesFound} files deleted.`);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || "Purge failed.");
    }
  };

  const overdue = (created) => created && (Date.now() - new Date(created.replace(" ", "T")).getTime()) > 15 * 24 * 3600 * 1000;

  return (
    <div className="admin-reports">
      <h2>Reports</h2>
      <p className="admin-reports-note">Acknowledge within 24 hours and resolve within 15 days (India IT Rules 2021). Reports older than 15 days are marked overdue.</p>
      {msg && <p className="admin-reports-ok" role="status">{msg}</p>}
      {err && <p className="admin-reports-err" role="alert">{err}</p>}

      <div className="admin-reports-filter">
        {["OPEN", "RESOLVED", "DISMISSED", "ALL"].map((s) => (
          <button key={s} className={status === s ? "active" : ""} onClick={() => setStatus(s)}>{s.charAt(0) + s.slice(1).toLowerCase()}</button>
        ))}
      </div>

      {loading ? <p>Loading…</p> : reports.length === 0 ? <p>No {status === "ALL" ? "" : status.toLowerCase()} reports.</p> : (
        <table className="admin-reports-table">
          <thead>
            <tr><th>Ref</th><th>Received</th><th>Reason</th><th>About</th><th>Reported by</th><th>Details</th><th>Status / action</th></tr>
          </thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id} className={r.status === "OPEN" && overdue(r.createdDate) ? "admin-reports-overdue" : ""}>
                <td>REP-{r.id}</td>
                <td>{fmt(r.createdDate)}{r.status === "OPEN" && overdue(r.createdDate) && <div className="admin-reports-flag">Overdue</div>}</td>
                <td>{REASON_LABEL[r.reason] || r.reason}</td>
                <td>
                  {r.targetType.toLowerCase().replace("_", " ")}{r.targetId ? ` #${r.targetId}` : ""}
                  {r.targetTitle && <div>{r.targetTitle}</div>}
                  {r.targetUserId && <div>posted by user #{r.targetUserId}</div>}
                  {r.targetUrl && <div><a href={r.targetUrl} target="_blank" rel="noopener noreferrer">Open page</a></div>}
                </td>
                <td>
                  {r.reporterName || (r.reporterUserId ? `user #${r.reporterUserId}` : "Guest")}
                  {r.reporterEmail && <div><a href={`mailto:${r.reporterEmail}?subject=Your SaatSaheli report REP-${r.id}`}>{r.reporterEmail}</a></div>}
                </td>
                <td style={{ whiteSpace: "pre-wrap", maxWidth: 260 }}>{r.details}</td>
                <td>
                  <div><strong>{r.status}</strong>{r.resolvedDate && <div>{fmt(r.resolvedDate)}</div>}</div>
                  <textarea rows={2} placeholder="Note (what you did)" defaultValue={r.adminNote || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))} />
                  <div className="admin-reports-actions">
                    {r.status !== "RESOLVED" && <button onClick={() => update(r.id, "RESOLVED")}>Resolve</button>}
                    {r.status !== "DISMISSED" && <button onClick={() => update(r.id, "DISMISSED")}>Dismiss</button>}
                    {r.status !== "OPEN" && <button onClick={() => update(r.id, "OPEN")}>Reopen</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2 style={{ marginTop: 28 }}>Closed accounts awaiting purge</h2>
      <p className="admin-reports-note">Users who deleted their account. Their sign-in and personal details are already gone and their content is hidden; purge permanently deletes their content, comments and uploaded files (payments and orders are kept). Promised within 30 days.</p>
      {purges.length === 0 ? <p>None.</p> : (
        <table className="admin-reports-table">
          <thead><tr><th>User</th><th>Closed</th><th>Purge by</th><th></th></tr></thead>
          <tbody>
            {purges.map((p) => (
              <tr key={p.userId}>
                <td>#{p.userId}</td>
                <td>{fmt(p.closedAt)}</td>
                <td>{fmt(p.purgeBy)}</td>
                <td><button onClick={() => purge(p.userId)}>Purge now</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
